import { type Pagination, type PaginationData, type VideoListItem, createVideoListItem } from 'models';
import { useInfiniteQuery } from 'query';
import { getRailsSortParameters } from 'common/utils/rails-parameters';
import type { VideoListItemDataObject } from 'models/service';
import type { VideosListFilter, VideosListSorter } from '../models';
import { getVideos } from '../services';
import { getVideosInfiniteQueryKey } from './keys';

type P = {
  filter?: VideosListFilter;
  pagination: Omit<Pagination, 'position'>;
  sort?: VideosListSorter;
};

export const useVideosInfiniteQuery = ({ filter, pagination, sort }: P) =>
  useInfiniteQuery<PaginationData<VideoListItem>, string | undefined>({
    async queryFn({ pageParam: cursor }) {
      const response = await getVideos({
        /* eslint-disable @typescript-eslint/naming-convention -- Start: Keys are defined by Backend API endpoint */
        params: {
          cursor,
          limit: pagination.pageSize,
          scope: filter?.scope,
          search: filter?.search,
          status: filter?.type,
          ...getRailsSortParameters({
            created_at: sort?.createdAt,
            name: sort?.name,
          }),
          featured: filter?.featured,
          organization_id: filter?.organizationId,
        },
        /* eslint-enable @typescript-eslint/naming-convention -- End: Keys are defined by Backend API endpoint */
      });

      return {
        data: response.data.data.map((videoDataObject: VideoListItemDataObject) =>
          createVideoListItem(videoDataObject),
        ),
        total: response.data.total,
      };
    },
    initialPageParam: undefined,
    getNextPageParam(lastPage, allPages) {
      if (allPages.length * pagination.pageSize >= lastPage.total) {
        return undefined;
      }

      return lastPage.data.at(-1)?.id;
    },
    queryKey: getVideosInfiniteQueryKey({ filter, pagination, sort }),
  });
